import { CompareLine } from '@/components/property-compare/compare-line';
import { PriceComparisionLineItem } from '@/components/property-compare/price-comparision-line-item';
import {
  getLandSizeInFt,
  getNumberRatio,
  soldInDays,
  soldInDaysString,
} from '@/components/property-compare/utils';
import { AppObject, Property } from '@/types';
import { transformProperty } from '@/utility';
import { FORMAT_DATE1, formatDate } from '@/utility/date';
import clsx from 'clsx';
import { GetServerSideProps } from 'next';
import { useRouter } from 'next/router';
import { useMemo, useRef, useState } from 'react';
import { LandComparisionLineItem } from '@/components/property-compare/land-comparision-line-item';
import { SoldInDaysComparisionLineItem } from '@/components/property-compare/sold-in-days-comparision-line-item';
import { BedroomComparisionLineItem } from '@/components/property-compare/bedroom-comparision-line-item';
import { PropertyAutocomplete } from '@/components/property-compare/autocomplete';
import { AmenityComparisionLine } from '@/components/property-compare/amenity-comparision-line';
import { validateToken } from '@/utility-backend/auth';
import { useAuth } from '@/context/auth';
import { Navbar2 } from '@/components/common/navbar';
import { useCompare } from '@/context/compare/hooks/use-compare';
import DeleteOutlinedIcon from '@/icons/delete-outlined';
import VisibilityOffOutlinedIcon from '@/icons/visiblity-off-outlined';
import BusinessOutlinedIcon from '@/icons/business-outlined';
import VisibilityOutlinedIcon from '@/icons/visiblity-outlined';
import { ReplierListingService } from '@/services/repliers';
import useBoundingClientRect from '@/hooks/use-bounding-client-rect';
import { useScrollPosition } from '@/hooks/use-scroll-position';

export interface PropertyCompareProps {
  properties: Property[];
}

const PropertyComparison: React.FC<ComparePageProps> = (props) => {
  const [showAll, setShowAll] = useState(false);
  const [hiddenItems, setHiddenItems] = useState<string[]>([]);
  const [masterProperty, setMasterProperty] = useState<Property>(null);
  const { isAuthenticated } = useAuth();
  const { removeItem, removeAll } = useCompare();
  const router = useRouter();

  const navbarRef = useRef(null);
  const bounds = useBoundingClientRect(navbarRef, 1000);
  const scroll = useScrollPosition(500);

  const properties = useMemo(
    () =>
      props.properties.filter((x) => !hiddenItems.includes(x.propertyNumber)),
    [props.properties, hiddenItems]
  );

  const handleMasterproperty = (property: Property) => {
    setMasterProperty(property);
  };

  const handleRemovePropery = (propertyNumber: string) => {
    if (masterProperty && propertyNumber === masterProperty.propertyNumber) {
      setMasterProperty(null);
    }

    removeItem(propertyNumber);

    router.replace(
      `/properties/compare?propertyNumbers=${props.properties
        .filter((x) => x.propertyNumber !== propertyNumber)
        .map((property) => property.propertyNumber)
        .join(',')}`
    );
  };

  const handleAddPropertyNumber = (propertyNumber: string) => {
    const propertyNumbers = props.properties.map(
      (property) => property.propertyNumber
    );
    if (propertyNumbers.includes(propertyNumber)) {
      return;
    }

    router.replace(
      `/properties/compare?propertyNumbers=${[
        ...propertyNumbers,
        propertyNumber,
      ].join(',')}`
    );
  };

  const handleHideItem = (propertyNumber: string) => {
    setHiddenItems((prev) => [...prev, propertyNumber]);
  };

  const handleUnhideAll = () => {
    setHiddenItems([]);
  };

  const handleRemoveAll = () => {
    setMasterProperty(null);
    removeAll();
    router.replace('/properties/compare');
  };

  if (!isAuthenticated) {
    return (
      <div className="flex flex-col">
        <Navbar2 title="Compare properties (Beta)" />
        <hr />
        <div className="flex items-center flex-1 flex-col py-28">
          <p className="text-2xl font-semibold text-gray-600">
            Please login to see comparison result
          </p>
        </div>
      </div>
    );
  }

  const navbar = (
    <div>
      <div
        ref={navbarRef}
        className={clsx(
          'fixed w-full bg-white z-50',
          scroll.top > 0 && 'shadow-md'
        )}
      >
        <Navbar2 title="Compare properties (Beta)" />
      </div>
      <div style={{ height: bounds?.height }}></div>
    </div>
  );

  if (props.properties.length < 1) {
    return (
      <div className="flex flex-col">
        {navbar}
        <hr />
        <div className="flex items-center justify-between mx-3">
          <h1 className="text-xl font-bold my-4">Compare Property</h1>
          <div className="flex flex-1 max-w-2xl ml-3">
            <PropertyAutocomplete
              onSelectPropertyNumber={handleAddPropertyNumber}
            />
          </div>
        </div>
        <div className="flex items-center flex-1 flex-col py-28 mx-3">
          <p className="text-2xl font-semibold text-gray-600">
            Select properties to compare
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      {navbar}
      <hr />
      <div className="flex items-center justify-between mx-3">
        <div className="flex flex-1 max-w-2xl py-2">
          <PropertyAutocomplete
            onSelectPropertyNumber={handleAddPropertyNumber}
          />
        </div>
        <div className="flex flex-row justify-end">
          <button
            className={clsx(
              'flex items-center justify-center px-2 py-1 rounded',
              'border-2',
              'outline-2 outline-primary-400',
              'bg-primary-50 text-primary-500',
              'hover:bg-primary-500 hover:text-white hover:border-primary-400',
              'active:bg-primary-600 active:text-white active:border-primary-500 active:outline active:z-10'
            )}
            style={{
              borderTopRightRadius: 0,
              borderBottomRightRadius: 0,
            }}
            title="Unhide all properties"
            onClick={handleUnhideAll}
          >
            <VisibilityOutlinedIcon />
          </button>
          <button
            className={clsx(
              'flex items-center justify-center px-2 py-1 rounded',
              'border-2',
              'outline-2 outline-red-400',
              'bg-red-50 text-red-500',
              'hover:bg-red-500 hover:text-white hover:border-red-400',
              'active:bg-red-600 active:text-white active:border-red-500 active:outline active:z-10'
            )}
            style={{
              borderLeftWidth: 0,
              borderTopLeftRadius: 0,
              borderBottomLeftRadius: 0,
            }}
            title="Remove all properties from comparison"
            onClick={handleRemoveAll}
          >
            <DeleteOutlinedIcon />
          </button>
        </div>
      </div>

      <div className="overflow-auto mx-3">
        <table
          className="border border-gray-300"
          style={{ borderCollapse: 'collapse' }}
        >
          <tbody>
            <tr>
              <td className="text-center">Action</td>
              {properties.map((property) => (
                <td
                  key={property.propertyNumber}
                  className={clsx(
                    'py-2 border border-gray-300 px-2 text-center',
                    property.propertyNumber ===
                      masterProperty?.propertyNumber && 'bg-gray-50'
                  )}
                >
                  {/* <input
                    className="h-5 w-5 form-radio text-primary-400"
                    type="radio"
                    name="masterProperty"
                    checked={
                      properties[index].propertyNumber ===
                      masterProperty?.propertyNumber
                    }
                    onChange={() =>
                      handleMasterproperty(properties[index])
                    }
                  /> */}
                  <div className="flex flex-row justify-end">
                    <button
                      className={clsx(
                        'flex items-center justify-center px-2 py-1 rounded',
                        'border-2',
                        'outline-2 outline-primary-400',
                        property.propertyNumber ===
                          masterProperty?.propertyNumber
                          ? 'bg-primary-400 text-white border-primary-300'
                          : 'bg-primary-50 text-primary-500',
                        'hover:bg-primary-500 hover:text-white hover:border-primary-400',
                        'active:bg-primary-600 active:text-white active:border-primary-500 active:outline active:z-10'
                      )}
                      style={{
                        borderTopRightRadius: 0,
                        borderBottomRightRadius: 0,
                      }}
                      title="Set as base property for comparison"
                      onClick={() => handleMasterproperty(property)}
                    >
                      <BusinessOutlinedIcon />
                    </button>
                    <button
                      className={clsx(
                        'flex items-center justify-center px-2 py-1',
                        'border-2',
                        'outline-2 outline-primary-400',
                        'bg-primary-50 text-primary-500',
                        'hover:bg-primary-500 hover:text-white hover:border-primary-400',
                        'active:bg-primary-600 active:text-white active:border-primary-500 active:outline active:z-10',
                        'disabled:text-gray-400 disabled:hover:bg-gray-50 disabled:border-gray-200 disabled:outline-none'
                      )}
                      style={{ borderLeftWidth: 0 }}
                      title="Hide from comparison"
                      onClick={() => handleHideItem(property.propertyNumber)}
                    >
                      <VisibilityOffOutlinedIcon />
                    </button>
                    <button
                      className={clsx(
                        'flex items-center justify-center px-2 py-1 rounded',
                        'border-2',
                        'outline-2 outline-red-400',
                        'bg-red-50 text-red-500',
                        'hover:bg-red-500 hover:text-white hover:border-red-400',
                        'active:bg-red-600 active:text-white active:border-red-500 active:outline active:z-10'
                      )}
                      style={{
                        borderLeftWidth: 0,
                        borderTopLeftRadius: 0,
                        borderBottomLeftRadius: 0,
                      }}
                      title="Remove from comparison"
                      onClick={() =>
                        handleRemovePropery(property.propertyNumber)
                      }
                    >
                      <DeleteOutlinedIcon />
                    </button>
                  </div>
                </td>
              ))}
            </tr>
            <CompareLine
              masterPropertyNumber={masterProperty?.propertyNumber}
              label={
                <div className="flex flex-1 justify-center items-center gap-2">
                  <input
                    type="checkbox"
                    checked={showAll}
                    onChange={() => setShowAll(!showAll)}
                    className="flex"
                  />
                  <span className="flex">Show all</span>
                </div>
              }
              properties={properties}
              valueExtractor={(property) => property.thumbnail}
              render={(
                value: string,
                _comparisionResult: number,
                property: Property
              ) => (
                <div className="relative flex h-full w-full justify-center items-center">
                  <img
                    src={value}
                    className=" h-32 w-96 px-1 object-contain rounded-lg"
                    alt={property.name}
                  />
                </div>
              )}
            />
            <CompareLine
              masterPropertyNumber={masterProperty?.propertyNumber}
              label="Property Number"
              properties={properties}
              valueExtractor={(property) => property.propertyNumber}
              render={(value: string, _comparisionResult: number, property) => (
                <div className="px-2 py-2 text-center">
                  <a
                    href={`/detail/${property.id}/${property.slug}`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-primary-500 hover:underline"
                  >
                    {value}
                  </a>
                </div>
              )}
            />
            <CompareLine
              hideIfEqual={!showAll && properties.length > 1}
              masterPropertyNumber={masterProperty?.propertyNumber}
              label="Address"
              properties={properties}
              valueExtractor={(property) => property.name}
              render={(value: string, _comparisionResult: number) => (
                <div className="px-2 py-2 text-center">{value}</div>
              )}
            />
            <CompareLine
              hideIfEqual={!showAll && properties.length > 1}
              masterPropertyNumber={masterProperty?.propertyNumber}
              label="City"
              properties={properties}
              valueExtractor={(property) => property.address.city}
              render={(value: string) => (
                <div className="px-2 py-2 text-center">{value}</div>
              )}
            />
            <CompareLine
              hideIfEqual={!showAll && properties.length > 1}
              masterPropertyNumber={masterProperty?.propertyNumber}
              label="Listed Date"
              properties={properties}
              valueExtractor={(property) =>
                formatDate(property.listDate, FORMAT_DATE1)
              }
              render={(value: string, _comparisionResult: number) => (
                <div className="px-2 py-2 text-center">{value}</div>
              )}
            />
            <CompareLine
              hideIfEqual={!showAll && properties.length > 1}
              masterPropertyNumber={masterProperty?.propertyNumber}
              label="Days On Market"
              properties={properties}
              valueExtractor={(property) => property.daysOnMarket}
              render={(value: string, _comparisionResult: number) => (
                <div className="px-2 py-2 text-center">{value}</div>
              )}
            />
            <CompareLine
              hideIfEqual={!showAll && properties.length > 1}
              masterPropertyNumber={masterProperty?.propertyNumber}
              label="Price"
              properties={properties}
              valueExtractor={(x) => x.listPrice}
              comparator={(properties, property) =>
                getNumberRatio(
                  property.listPrice,
                  Math.max(...properties.map((x) => x.listPrice))
                )
              }
              render={(value: number, comparisionResult, property) => (
                <PriceComparisionLineItem
                  value={value}
                  comparisionResult={comparisionResult}
                  masterProperty={masterProperty}
                  property={property}
                  field="listPrice"
                />
              )}
            />
            {/* <CompareLine
              hideIfEqual={!showAll && properties.length > 1}
              masterProperty={master}
              label="Beds"
              properties={properties}
              valueExtractor={(x) => x.bedroomString}
              comparator={(properties, property) =>
                isNaN(Number(property.beds)) ? 0 : Number(property.beds)
              }
              render={(value: string, comparisionResult) => (
                <CountComparisionLineItem
                  value={value}
                  comparisionResult={comparisionResult}
                />
              )}
            /> */}
            <CompareLine
              hideIfEqual={!showAll && properties.length > 1}
              masterPropertyNumber={masterProperty?.propertyNumber}
              label="Beds"
              properties={properties}
              valueExtractor={(x) => x.bedroomString}
              comparator={(properties, property) =>
                isNaN(Number(property.beds)) ? 0 : Number(property.beds)
              }
              render={(value: string, comparisionResult, property) => (
                <BedroomComparisionLineItem
                  value={value}
                  comparisionResult={comparisionResult}
                  masterProperty={masterProperty}
                  property={property}
                />
              )}
            />
            <CompareLine
              hideIfEqual={!showAll && properties.length > 1}
              masterPropertyNumber={masterProperty?.propertyNumber}
              label="Baths"
              properties={properties}
              valueExtractor={(x) => x.bathroomString}
              comparator={(properties, property) =>
                isNaN(Number(property.bath)) ? 0 : Number(property.bath)
              }
              render={(value: string) => (
                // <CountComparisionLineItem
                //   value={value}
                //   comparisionResult={comparisionResult}
                // />
                <div className="px-2 py-2 text-center">{value}</div>
              )}
            />
            <CompareLine
              hideIfEqual={!showAll && properties.length > 1}
              masterPropertyNumber={masterProperty?.propertyNumber}
              label="Parking"
              properties={properties}
              valueExtractor={(x) => x.parkingString}
              comparator={(properties, property) =>
                isNaN(Number(property.parking)) ? 0 : Number(property.parking)
              }
              render={(value: string) => (
                // <CountComparisionLineItem
                //   value={value}
                //   comparisionResult={comparisionResult}
                // />
                <div className="px-2 py-2 text-center">{value}</div>
              )}
            />
            <CompareLine
              hideIfEqual={!showAll && properties.length > 1}
              masterPropertyNumber={masterProperty?.propertyNumber}
              label="Annual Taxes"
              properties={properties}
              valueExtractor={(x) => x.annualTaxes || 0}
              comparator={(properties, property) =>
                getNumberRatio(
                  property.annualTaxes || 0,
                  Math.max(...properties.map((x) => x.annualTaxes || 0))
                )
              }
              render={(value: number, comparisionResult, property) => (
                <PriceComparisionLineItem
                  value={value}
                  comparisionResult={comparisionResult}
                  danger
                  masterProperty={masterProperty}
                  property={property}
                  field="annualTaxes"
                />
              )}
            />
            <CompareLine
              hideIfEqual={!showAll && properties.length > 1}
              masterPropertyNumber={masterProperty?.propertyNumber}
              label="Land Size"
              properties={properties}
              valueExtractor={(x) => x.landSizeString}
              comparator={(properties, property) =>
                getNumberRatio(
                  getLandSizeInFt(property),
                  Math.max(...properties.map((x) => getLandSizeInFt(x)))
                )
              }
              render={(value: string, comparisionResult, property) => (
                <LandComparisionLineItem
                  value={value}
                  comparisionResult={comparisionResult}
                  masterProperty={masterProperty}
                  property={property}
                />
              )}
            />
            <CompareLine
              hideIfEqual={!showAll && properties.length > 1}
              masterPropertyNumber={masterProperty?.propertyNumber}
              label="Sold Price"
              properties={properties}
              valueExtractor={(x) => x.soldPrice || 0}
              comparator={(properties, property) =>
                getNumberRatio(
                  property.soldPrice || 0,
                  Math.max(...properties.map((x) => x.soldPrice || 0))
                )
              }
              render={(value: number, comparisionResult, property) => (
                <PriceComparisionLineItem
                  value={value}
                  comparisionResult={comparisionResult}
                  property={property}
                  field="soldPrice"
                />
              )}
            />
            <CompareLine
              hideIfEqual={!showAll && properties.length > 1}
              masterPropertyNumber={masterProperty?.propertyNumber}
              label="Sold Date"
              properties={properties}
              valueExtractor={(property) =>
                property.soldDate
                  ? formatDate(property.soldDate, FORMAT_DATE1)
                  : '-'
              }
              render={(value: string) => (
                <div className="px-2 py-2 text-center">{value}</div>
              )}
            />
            <CompareLine
              hideIfEqual={!showAll && properties.length > 1}
              masterPropertyNumber={masterProperty?.propertyNumber}
              label="Sold In"
              properties={properties}
              valueExtractor={(property) => soldInDaysString(property) || '-'}
              comparator={(properties, property) => soldInDays(property)}
              render={(value: string, comparisionResult) => (
                <SoldInDaysComparisionLineItem
                  value={value}
                  comparisionResult={comparisionResult}
                />
              )}
            />
            <CompareLine
              hideIfEqual={!showAll && properties.length > 1}
              masterPropertyNumber={masterProperty?.propertyNumber}
              label="Property Type"
              properties={properties}
              valueExtractor={(property) => property.propertyTypeString}
              render={(value: string) => (
                <div className="px-2 py-2 text-center">{value}</div>
              )}
            />
            <CompareLine
              hideIfEqual={!showAll && properties.length > 1}
              masterPropertyNumber={masterProperty?.propertyNumber}
              label="Building Type"
              properties={properties}
              valueExtractor={(property) => property.propertyClass}
              render={(value: string) => (
                <div className="px-2 py-2 text-center">{value}</div>
              )}
            />
            <CompareLine
              hideIfEqual={!showAll && properties.length > 1}
              masterPropertyNumber={masterProperty?.propertyNumber}
              label="Description"
              properties={properties}
              valueExtractor={(property) => property.description}
              render={(value: string) => (
                <div className="px-2 py-2 text-center">{value}</div>
              )}
            />
            <AmenityComparisionLine
              hideIfEqual={!showAll && properties.length > 1}
              masterPropertyNumber={masterProperty?.propertyNumber}
              label="Ammenities"
              properties={properties}
            />
            {/* valueExtractor={(property) => property.nearByAmenties.join(', ')}
              render={(value: string) => (
                <div className="px-2 py-2 text-center">{value}</div>
                <AmenityComparisionLine
                />
              )} */}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PropertyComparison;

interface ComparePageProps {
  properties: Property[];
}

export const getServerSideProps: GetServerSideProps<ComparePageProps> = async (
  context
) => {
  if (!validateToken(context.req.cookies.jdr_token)) {
    return {
      props: {
        properties: [],
      },
    };
  }

  const propertyNumbers: string[] =
    (context.query.propertyNumbers as string)?.split(',').filter(Boolean) || [];

  if (!propertyNumbers.length) {
    return {
      props: {
        properties: [],
      },
    };
  }

  const query: AppObject = {
    propertyNumber: propertyNumbers,
    fields: 'boardId,propertyNumber',
  };

  query.status = ['A', 'U'];

  const client = new ReplierListingService();
  const response = await client.findListings(query);

  if (!response?.listings.length) {
    return {
      props: {
        properties: [],
      },
    };
  }

  const data = await Promise.all(
    response.listings.map((property: Property) => {
      return client.getListing(property.propertyNumber, property.boardId);
    })
  );

  const properties = data.map((item) =>
    transformProperty(item, context.locale)
  );

  return {
    props: {
      properties,
    },
  };
};
